import { useEffect, useState } from "react";
import type { ReactNode } from "react";

import { APPRAISAL_SUPPORT_ENABLED, EXTENSION_CAPTURE_ENABLED, METHOD_PAGE_ENABLED } from "../flags";
import { themeVar as colors, radius, type as typeTokens } from "../design-tokens";
import { useModuleStatus } from "../lib/useModuleStatus";
import { useSession } from "../lib/useSession";
import { AppShell } from "../ui/AppShell";
import { blockStyle } from "../ui/Block";
import { useDocumentTitle, vbTitle } from "../ui/useDocumentTitle";
import { listBriefs } from "../briefs/api";
import { briefTitle, kindLabel, shortDate, statusLabel } from "../briefs/format";
import type { BriefRow } from "../briefs/types";
import { NewBriefChooser } from "./NewBriefChooser";
import { cotalityServiceLine } from "../cma/cascade";
import { UsageTile } from "../ui/UsageWidgets";

/**
 * ================================================================================================
 * `/app` — THE SIGNED-IN FRONT DOOR  (S03, 2026-09-22)
 * ================================================================================================
 *
 * THE DEFECT THIS CLOSES. Signed out, `/` is the marketing page and that is correct. Signed IN,
 * there was no app home at all: every door dropped the user straight into building a CMA/AMB.
 * `screens/LoginScreen::safeNextPath` returned `"/cma"` in all three of its branches;
 * `marketing/LandingPage` and both "Open the app" buttons in `marketing/sections.tsx` pointed at
 * `/cma`; the shell's own header carried `<a href="/cma" class="btn p">`. So a signed-in user never
 * saw what else the product does, and had no way back to a start — the only route "home" was the
 * wordmark, which left the app for the marketing page.
 *
 * WHY `/app` AND NOT `/home`. The word "home" is already taken, and taken by the other page: the
 * shell's wordmark is labelled "ValuBrief — home" and points at `/`, the marketing nav's mark
 * points at `#top`. A second address also called home would make "home" ambiguous in the one place
 * it has to be unambiguous. `/app` is also what every entry point already CALLS this place — the
 * buttons say "Open the app" — so after this change the link text and the address finally agree.
 *
 * WHAT IS ON IT, AND WHY NOT MORE. Every card here is a route that will SERVE when it is clicked,
 * under exactly the gate the router puts on it: the build flags for `/appraisal`, `/method` and
 * `/extension`, the server probe for `/briefs` and `/crm`, and `is_admin` for `/users`. That is the
 * same rule `ui/AppShell`'s bar follows — it grows only as the reachable routes do — and it is the
 * no-dead-controls rule applied to a grid instead of a bar. A card that would 403, 404 or redirect
 * is not rendered; where withholding it needs an explanation, the explanation is rendered in its
 * place (see the admin note below).
 *
 * DELIBERATELY NOT HERE:
 *   - `/login`, `/reset`, `/forgot` — doors for somebody who is not signed in. On this page they
 *     are noise at best.
 *   - `/people` — a redirect kept for a name this app used for half a day. Its destination is
 *     `/users`, which IS here.
 *   - `/editor/:jobId` — also a redirect. `App.tsx`'s own docstring records that the editor's way
 *     in is `/cma/{id}?view=document`, i.e. from the brief, not from a second address.
 *   - `/editor` with no id — the fixture harness. It is a development surface and says so on its
 *     face; a front door is not where a made-up brief belongs.
 *   - `/briefs/property/:propertyKey` — reached FROM a brief, and meaningless without one.
 *   - `/result-demo` — a design-review screen over `mocks/recommendation.mock`.
 *   - `/cma` — that is the primary action, not a destination card.
 *
 * `/appraisal` IS HERE, AND THE OWNER'S BRIEF OMITTED IT. `APPRAISAL_SUPPORT_ENABLED` is `true`
 * (`src/flags.ts:215`), so the route serves today, and appraisal support is one of the three
 * verticals — leaving it off the front door while the other two are named in the chooser is
 * precisely the "one vertical is primary" failure the charter forbids. Its card says what it opens:
 * it lists the user's own finished briefs, each opening as a workfile (S10a, 2026-09-23). It used to
 * render the engineering fixture — an invented subject — and this card said "shown on a worked
 * example" to match; the fixture now lives at `/appraisal/_fixture` for admins and dev builds only.
 *
 * THE GREETING NEVER GUESSES A NAME. `GET /api/me` returns `user.name`, which is nullable. When it
 * is absent the greeting is simply "Welcome back" — never "there", never "User". A placeholder
 * name is a figure with no source wearing a friendlier hat.
 */

/**
 * ONE CALM LINE WHEN THE PUBLIC-RECORDS LOOKUP IS NOT LIVE. Nothing when it is.
 *
 * WHAT THIS IS NOT. It is not a status page, not an alert, and not a banner. No `role="alert"`,
 * no caution colour, no icon, no flag name. The agent cannot act on `VALUBRIEF_COTALITY_ENABLED`
 * and naming an internal switch on a customer surface is the mistake `CascadePanels`'
 * `INTERNAL_ONLY_KINDS` exists to correct. It is one sentence, in secondary type, saying what
 * still works.
 *
 * IT ASKS `/api/cotality/status`, WHICH IS THE CHEAP QUESTION. That route makes no external call
 * and spends none of the daily allowance — it reads local config and the local circuit breaker.
 * `/api/next/cascade/status` would answer too, but it is auth-gated and does more work for an
 * answer this page only needs one field of.
 *
 * A FAILED FETCH SAYS NOTHING. If the status call itself does not come back we know nothing about
 * the provider, and "we could not check" is not a service notice — it is us talking about
 * ourselves on somebody's front door. `state` stays null and this renders null.
 */
function ServiceNotice() {
  const [line, setLine] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    // `Promise.resolve().then(...)` rather than a bare `fetch(...)`: a `fetch` that throws
    // SYNCHRONOUSLY — which a stubbed one in a test does, and a blocked one in a hardened browser
    // can — would escape the effect and take the whole front door down with it. A service notice
    // must never be able to break the page it is a footnote on.
    // ONLY FOR THE PROVIDER-DIAGNOSTICS ALLOW-LIST (owner, 2026-10-19). The signed-in cascade
    // status says whether this viewer is on it (`provider_diag`, server-side); nobody else is told
    // a provider's state on the front door, and their browser does not even ask.
    Promise.resolve()
      .then(() => fetch("/api/next/cascade/status", { credentials: "same-origin" }))
      .then((r) => (r.ok ? r.json() : null))
      .then((st: { provider_diagnostics?: boolean } | null) =>
        st?.provider_diagnostics === true
          ? fetch("/api/cotality/status", { credentials: "same-origin" })
          : null)
      .then((r) => (r && r.ok ? r.json() : null))
      .then((body: { state?: string; available?: boolean } | null) => {
        if (!live || !body) return;
        setLine(cotalityServiceLine(body));
      })
      .catch(() => {
        /* We learned nothing, so we say nothing. See the docstring. */
      });
    return () => {
      live = false;
    };
  }, []);

  if (!line) return null;
  return (
    <p
      role="status"
      data-testid="home-service-notice"
      style={{
        margin: "0 0 20px",
        fontSize: typeTokens.scale.dense,
        lineHeight: 1.55,
        color: colors.textSecondary,
        ...blockStyle("copy"),
      }}
    >
      {line}
    </p>
  );
}

/** One destination card's content and the gate that decides whether it exists at all. */
interface Destination {
  to: string;
  name: string;
  line: string;
  show: boolean;
}

/** How many of your own briefs the home page offers before sending you to the full shelf. */
const RECENT_LIMIT = 5;

export function AppHome() {
  useDocumentTitle(vbTitle("Home"));

  const session = useSession();
  const { crmEnabled, briefsEnabled, settled, crmCounts } = useModuleStatus();
  const [chooserOpen, setChooserOpen] = useState(false);
  /* WHETHER THIS PERSON HAS BEEN HERE BEFORE, AS THE SHELF KNOWS IT (B4, 2026-09-23). "Welcome back"
     greeted a brand-new account on its first visit, which is false. `null` = not known yet. */
  const [hasBriefs, setHasBriefs] = useState<boolean | null>(null);

  const name = session.name?.trim() || null;

  const destinations: Destination[] = [
    {
      to: "/briefs",
      name: "Your briefs",
      line: "Everything your organization has generated, newest first, with a link back to each report.",
      show: briefsEnabled,
    },
    {
      to: "/crm",
      name: "CRM",
      line: "Parcels, parties, engagements and the deadlines attached to them.",
      show: crmEnabled,
    },
    {
      to: "/appraisal",
      name: "Appraisal support",
      line: "Open a finished brief as a workfile for the licensed appraiser.",
      show: APPRAISAL_SUPPORT_ENABLED,
    },
    {
      to: "/method",
      name: "Method",
      line: "How one finished brief reached its number, out of that brief's own payload.",
      show: METHOD_PAGE_ENABLED,
    },
    {
      to: "/extension",
      name: "Browser extension",
      line: "The capture credential the extension signs in with.",
      show: EXTENSION_CAPTURE_ENABLED,
    },
    {
      to: "/branding",
      name: "Branding",
      line: "The logo, colors and contact details printed on every report you send.",
      show: true,
    },
    {
      to: "/users",
      name: "Users",
      line: "Add a colleague, change a role, or remove access. Administrators only.",
      show: session.isAdmin,
    },
  ];

  const shown = destinations.filter((d) => d.show);

  return (
    <AppShell step="home" content="form">
      <div style={{ padding: "8px 0 48px" }}>
        <header style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: typeTokens.scale.lg, fontWeight: 600, margin: "0 0 4px" }}>
            {/* "back" only once the shelf shows there has been a before; "Welcome" is true either way,
                so it is also what shows while the shelf is still loading. */}
            {`${hasBriefs === true ? "Welcome back" : "Welcome"}${name ? `, ${name}` : ""}`}
          </h1>
          <p style={{ fontSize: typeTokens.scale.base, color: colors.textSecondary, margin: 0, ...blockStyle("copy") }}>
            Start a brief, or pick up where you left off.
          </p>
        </header>

        {/* ============================================================================
            SERVICE NOTICE SLOT — FILLED BY SLICE S05 (2026-09-22).
            S03 left this position empty on purpose and said why: a placeholder banner
            is a claim about the service that nobody made. `ServiceNotice` keeps that
            rule — it renders NOTHING when the provider is live, nothing when the server
            did not say, and nothing when the connector is merely switched off on a
            deployment that never intended to use it. One calm sentence otherwise.
            ============================================================================ */}
        <ServiceNotice />

        {/* THE PRIMARY ACTION, FIRST (2026-10-18). It sat third, under the usage tile, as a 32px
            button after a paragraph — measured in `before/home@1440.png` the first card on the
            page was the report count. Now it is the first thing under the greeting, in a card of
            its own, with the sentence that says what it does beside it. */}
        <section aria-labelledby="vb-home-start" className="vb-home-start">
          <div>
            <h2 id="vb-home-start" className="vb-home-h2">Start something</h2>
            <p className="vb-home-start-line">
              One brief serves all three verticals. Choose the report and the builder opens with it.
            </p>
          </div>
          <button type="button" className="btn p vb-home-primary" onClick={() => setChooserOpen(true)}>
            New brief
          </button>
        </section>

        {/* WHERE YOU LEFT OFF, AND WHAT IS WAITING (2026-10-18). Your own recent briefs beside the
            CRM's open work, side by side on a wide screen and stacked on a phone. */}
        <div className="vb-home-split">
          <RecentBriefs enabled={briefsEnabled} settled={settled}
                        onLoaded={(count) => setHasBriefs(count > 0)} />
          {crmEnabled && <CrmShortcuts counts={crmCounts ?? null} />}
        </div>

        {/* THE MONTHLY REPORT LIMIT TILE (§6, board 1 option B). `id="usage"` is the anchor the
            top-bar pill's link (`ui/UsageWidgets::UsagePillView`) scrolls to or navigates to.
            Renders nothing while it has not yet heard from the server and nothing on a failed
            round trip — the same "we learned nothing, so we say nothing" rule `ServiceNotice`
            above follows — so there is no flash of a zero that looks like a real count. */}
        <UsageTile />

        <section aria-labelledby="vb-home-go" style={{ marginBottom: 28 }}>
          <h2
            id="vb-home-go"
            style={{ fontSize: typeTokens.scale.md, fontWeight: 600, margin: "0 0 12px", color: colors.textPrimary }}
          >
            Where to go
          </h2>
          {hasBriefs === false ? (
            /* NO BRIEFS YET (polish-primary-actions, #10): four cards of prose around an empty shelf
               gave the first screen no single job. The same destinations, the same links, on one
               line; once the shelf has a brief the cards below come back unchanged. */
            <p data-testid="vb-home-go-line"
               style={{ margin: 0, fontSize: typeTokens.scale.base, lineHeight: 1.8, color: colors.textSecondary, ...blockStyle("copy") }}>
              {shown.map((destination, i) => (
                <span key={destination.to}>
                  {i > 0 && <span aria-hidden="true"> · </span>}
                  <a href={destination.to} title={destination.line} style={{ color: colors.accent }}>{destination.name}</a>
                </span>
              ))}
            </p>
          ) : (
          <ul
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 12,
              margin: 0,
              padding: 0,
              listStyle: "none",
            }}
          >
            {shown.map((destination) => (
              <li key={destination.to} style={{ display: "flex" }}>
                <a
                  href={destination.to}
                  className="vb-home-card"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    width: "100%",
                    padding: "14px 16px 15px",
                    borderRadius: radius.card,
                    border: `1px solid ${colors.border}`,
                    background: colors.bgSurface,
                    color: colors.textPrimary,
                    textDecoration: "none",
                  }}
                >
                  <span style={{ fontSize: typeTokens.scale.base, fontWeight: 600 }}>{destination.name}</span>
                  <span style={{ fontSize: typeTokens.scale.sm, lineHeight: 1.5, color: colors.textSecondary }}>
                    {destination.line}
                  </span>
                </a>
              </li>
            ))}
          </ul>
          )}
          {/* SAYS WHY, IN PLACE OF THE CARD IT IS ABOUT. A designated administrator whose session
              could not clear its second factor is not "not an administrator" — the route would
              still refuse them, so the card is correctly absent, but the reason is worth one
              quiet line. Without it the People screen simply vanishes for the person who most
              expects it. `adminPendingMfa` is the server's own distinction (`app.py::_me`). */}
          {session.adminPendingMfa && (
            <p
              style={{
                margin: "12px 0 0",
                fontSize: typeTokens.scale.dense,
                color: colors.textSecondary,
                ...blockStyle("copy"),
              }}
            >
              Users is open to administrators. This session has not finished its second step, so it
              is not shown — sign in again to complete it.
            </p>
          )}
        </section>


        {/* THE WAY BACK TO THE PUBLIC SITE. `/` now sends a signed-in visitor here, which is the
            point of this page — but that must not make valubrief.ai unreachable from inside the
            product. `?site=1` is the one thing `marketing/LandingPage` checks before redirecting,
            so this link lands on the marketing page and stays there. */}
        <footer style={{ marginTop: 32, paddingTop: 14, borderTop: `1px solid ${colors.border}` }}>
          <a href="/?site=1" style={{ fontSize: typeTokens.scale.dense, color: colors.textSecondary }}>
            The public site
          </a>
        </footer>
      </div>

      <NewBriefChooser open={chooserOpen} onClose={() => setChooserOpen(false)} />
    </AppShell>
  );
}

/* --------------------------------------------------------------------------- *
 * Your recent briefs                                                            *
 * --------------------------------------------------------------------------- */

/**
 * `mine: true`, not the shelf's org-wide default. The shelf at `/briefs` is org-scoped on purpose —
 * a colleague must be able to open a brief their own firm paid to produce — but "pick up where you
 * left off" is a statement about THIS person's work, and a home page that opened with five briefs a
 * colleague made would answer a question nobody asked. The heading says whose they are and the
 * link beside it goes to all of them.
 *
 * THE FIGURE IS NOT HERE. `BriefRow.headline_value` is a cached display copy that carries no
 * provenance, and `briefs/BriefsScreen` is explicit that nothing on a list is a number a client
 * should be shown. A front door is the last place to start. Each row carries the address, the date
 * and the state, and the number is in the report where it has its chip and its drawer.
 */
function RecentBriefs({
  enabled,
  settled,
  onLoaded,
}: {
  enabled: boolean;
  settled: boolean;
  onLoaded?: (count: number) => void;
}) {
  const [rows, setRows] = useState<BriefRow[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    listBriefs({ mine: true, limit: RECENT_LIMIT, signal: controller.signal })
      .then((page) => { setRows(page.items); onLoaded?.(page.items.length); })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setFailed(true);
      });
    return () => controller.abort();
  }, [enabled]);

  const heading = (
    <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
      <h2
        id="vb-home-recent"
        style={{ fontSize: typeTokens.scale.md, fontWeight: 600, margin: 0, color: colors.textPrimary }}
      >
        Your recent briefs
      </h2>
      {enabled && (
        <a href="/briefs" className="vb-home-more">
          All briefs
        </a>
      )}
    </div>
  );

  let body: ReactNode;
  if (!enabled) {
    // SAYS WHY, IN PLACE. The shelf's routes 404 when `reports_enabled` is off, so there is no list
    // to show and no link worth offering — but a section that simply disappeared would read as a
    // rendering fault. It waits for the probe first: "not asked yet" is not "the answer was no".
    body = settled ? (
      <p style={{ margin: 0, fontSize: typeTokens.scale.base, color: colors.textSecondary }}>
        The brief shelf is switched off for this deployment, so there is no list to show here.
      </p>
    ) : null;
  } else if (failed) {
    body = (
      <p style={{ margin: 0, fontSize: typeTokens.scale.base, color: colors.textSecondary }}>
        Your briefs could not be loaded just now. <a href="/briefs" style={{ color: colors.accent }}>Open the shelf</a> to
        try again.
      </p>
    );
  } else if (rows === null) {
    body = (
      <p style={{ margin: 0, fontSize: typeTokens.scale.base, color: colors.textSecondary }}>Loading your briefs…</p>
    );
  } else if (rows.length === 0) {
    // ONE LINE AND A WAY TO ACT — never an empty box with headings over nothing.
    body = (
      <p style={{ margin: 0, fontSize: typeTokens.scale.base, color: colors.textSecondary, ...blockStyle("copy") }}>
        You haven&apos;t made a brief yet. Start one with the New brief button above.
      </p>
    );
    /* THE FIRST RUN TEACHES (B4, 2026-09-23). A brand-new account arrives here with nothing on the
       shelf, and the one line above said what to press but not what happens next. Three short
       steps, words only — no new control, and no door to a demo workspace that does not exist yet
       (B1 is parked on the schema); an unbuilt destination offered here would be a dead control. */
    body = (
      <>
        {body}
        <ol data-testid="vb-home-first-run"
            style={{ margin: "10px 0 0", paddingLeft: 20, fontSize: typeTokens.scale.sm, lineHeight: 1.6,
                     color: colors.textSecondary, ...blockStyle("copy") }}>
          <li>Type the property&apos;s address. We pull what the public record holds.</li>
          <li>Check what we found. Every figure shows where it came from, and you can correct it.</li>
          <li>Choose the report — a CMA or an Advanced Market Brief — and it is built from what you confirmed.</li>
        </ol>
      </>
    );
  } else {
    body = (
      <ul style={{ margin: 0, padding: 0, listStyle: "none", ...blockStyle("copy") }}>
        {rows.map((row) => (
          <li key={row.id} style={{ borderTop: `1px solid ${colors.border}` }}>
            <RecentRow row={row} />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <section aria-labelledby="vb-home-recent" style={{ marginBottom: 8 }}>
      {heading}
      {body}
    </section>
  );
}

/**
 * One row. `view_path` is resolved SERVER-SIDE and may be null when this deployment has no viewer
 * for the store that holds the report; `view_note` says which and why. A row in that state prints
 * the note instead of a link, because a link that goes nowhere is the dead end this whole page
 * exists to remove.
 */
function RecentRow({ row }: { row: BriefRow }) {
  const title = briefTitle(row);
  const when = shortDate(row.generated_at ?? row.created_at);
  const state = statusLabel(row.status);
  // What kind of brief leads the line, as the shelf names it (2026-10-18). Still no figure: see above.
  const meta = [kindLabel(row.kind), when, state].filter(Boolean).join(" · ");

  const label = (
    <>
      <span
        style={{
          fontSize: typeTokens.scale.base,
          fontWeight: title.isPlaceholder ? 400 : 600,
          color: title.isPlaceholder ? colors.textSecondary : colors.textPrimary,
        }}
      >
        {title.text}
      </span>
      {meta && (
        <span style={{ fontSize: typeTokens.scale.sm, color: colors.textSecondary }}>{meta}</span>
      )}
    </>
  );

  if (!row.view_path) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 2, padding: "10px 2px" }}>
        {label}
        <span style={{ fontSize: typeTokens.scale.sm, color: colors.textSecondary }}>
          {row.view_note || "There is no viewer for this report in this deployment."}
        </span>
      </div>
    );
  }

  return (
    <a
      href={row.view_path}
      className="vb-home-row"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        padding: "10px 2px",
        textDecoration: "none",
        color: colors.textPrimary,
      }}
    >
      {label}
    </a>
  );
}

/* --------------------------------------------------------------------------- *
 * The CRM's open work                                                           *
 * --------------------------------------------------------------------------- */

/**
 * FOUR WAYS INTO THE CRM, WITH THE RAIL'S OWN NUMBERS (2026-10-18). The counts are `/status`'s,
 * from the same response that decided the CRM link is shown, and they are labeled exactly as the
 * CRM's rail labels them, so the home page and the CRM never disagree about what "4" means. A count
 * the server did not send prints an em dash and says so; it is never a zero.
 */
const CRM_SHORTCUTS: { key: string; label: string; to: string }[] = [
  { key: "engagements", label: "Engagements", to: "/crm" },
  { key: "deadlines", label: "Deadlines", to: "/crm/deadlines" },
  { key: "facts", label: "Facts to review", to: "/crm/facts" },
  { key: "parcels", label: "Parcels", to: "/crm/parcels" },
];

function CrmShortcuts({ counts }: { counts: Record<string, number> | null }) {
  const missing = CRM_SHORTCUTS.some((c) => typeof counts?.[c.key] !== "number");
  return (
    <section aria-labelledby="vb-home-crm" className="vb-home-crm">
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
        <h2 id="vb-home-crm" className="vb-home-h2" style={{ margin: 0 }}>Your CRM</h2>
        <a href="/crm" className="vb-home-more">Open the CRM</a>
      </div>
      <ul className="vb-home-crm-list">
        {CRM_SHORTCUTS.map((c) => {
          const n = counts?.[c.key];
          return (
            <li key={c.key}>
              <a href={c.to} className="vb-home-crm-link">
                <span>{c.label}</span>
                {typeof n === "number"
                  ? <span className="vb-home-crm-count">{n}</span>
                  : <span className="vb-home-crm-count" title="This count did not load.">—</span>}
              </a>
            </li>
          );
        })}
      </ul>
      {missing && (
        <p style={{ margin: "8px 0 0", fontSize: typeTokens.scale.sm, color: colors.textSecondary }}>
          A dash means that count did not load. It is not a zero; the lists themselves are in the CRM.
        </p>
      )}
    </section>
  );
}
